/**
 * Responsive Utilities for PCKM Mobile
 * Provides helpers for responsive spacing, font sizes, and screen detection
 */

import { Dimensions, PixelRatio } from 'react-native';
import { spacing, typography } from './theme';

// Get screen dimensions
const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Breakpoints for different device sizes
export const breakpoints = {
  small: 375,   // Small phones (iPhone SE, etc.)
  medium: 414,  // Standard phones (iPhone 11, 12, 13, etc.)
  large: 768,   // Tablets
  xlarge: 1024, // Large tablets
};

// Screen size categories
export type ScreenSize = 'small' | 'medium' | 'large' | 'xlarge';

/**
 * Get the current screen size category
 */
export const getScreenSize = (): ScreenSize => {
  if (SCREEN_WIDTH >= breakpoints.xlarge) return 'xlarge';
  if (SCREEN_WIDTH >= breakpoints.large) return 'large';
  if (SCREEN_WIDTH >= breakpoints.medium) return 'medium';
  return 'small';
};

/**
 * Check if the current device is a specific size or larger
 */
export const isScreenSize = (size: ScreenSize): boolean => {
  const currentSize = getScreenSize();
  const sizes: ScreenSize[] = ['small', 'medium', 'large', 'xlarge'];
  const currentIndex = sizes.indexOf(currentSize);
  const targetIndex = sizes.indexOf(size);
  return currentIndex >= targetIndex;
};

/**
 * Scale a value based on screen width
 * Reference width is iPhone 11 (414px)
 */
export const scale = (size: number): number => {
  const referenceWidth = 414;
  return (SCREEN_WIDTH / referenceWidth) * size;
};

/**
 * Scale with moderation - prevents extreme scaling on very large/small screens
 * @param size - The base size to scale
 * @param factor - How much to moderate the scaling (0-1, where 1 = full scaling, 0 = no scaling)
 */
export const moderateScale = (size: number, factor: number = 0.5): number => {
  const scaled = scale(size);
  return size + (scaled - size) * factor;
};

/**
 * Get responsive spacing value
 * Automatically scales based on screen size
 */
export const getResponsiveSpacing = (
  spacingKey: keyof typeof spacing
): number => {
  const baseValue = spacing[spacingKey];
  const screenSize = getScreenSize();

  // Scale factors for different screen sizes
  const scaleFactor = {
    small: 0.85,   // 15% smaller on small screens
    medium: 1,     // Base size
    large: 1.15,   // 15% larger on tablets
    xlarge: 1.3,   // 30% larger on large tablets
  };

  return Math.round(baseValue * scaleFactor[screenSize]);
};

/**
 * Get responsive font size
 * Automatically scales based on screen size with moderation
 */
export const getResponsiveFontSize = (
  fontSizeKey: keyof typeof typography.fontSize
): number => {
  const baseValue = typography.fontSize[fontSizeKey];
  const screenSize = getScreenSize();

  // More conservative scaling for fonts
  const scaleFactor = {
    small: 0.92,   // 8% smaller on small screens
    medium: 1,     // Base size
    large: 1.08,   // 8% larger on tablets
    xlarge: 1.15,  // 15% larger on large tablets
  };

  return Math.round(baseValue * scaleFactor[screenSize]);
};

/**
 * Get responsive padding object
 * Returns padding values that scale with screen size
 */
export const getResponsivePadding = (options: {
  vertical?: keyof typeof spacing;
  horizontal?: keyof typeof spacing;
  top?: keyof typeof spacing;
  bottom?: keyof typeof spacing;
  left?: keyof typeof spacing;
  right?: keyof typeof spacing;
}) => {
  const result: {
    paddingTop?: number;
    paddingBottom?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingVertical?: number;
    paddingHorizontal?: number;
  } = {};

  if (options.vertical) {
    result.paddingVertical = getResponsiveSpacing(options.vertical);
  }
  if (options.horizontal) {
    result.paddingHorizontal = getResponsiveSpacing(options.horizontal);
  }
  if (options.top) {
    result.paddingTop = getResponsiveSpacing(options.top);
  }
  if (options.bottom) {
    result.paddingBottom = getResponsiveSpacing(options.bottom);
  }
  if (options.left) {
    result.paddingLeft = getResponsiveSpacing(options.left);
  }
  if (options.right) {
    result.paddingRight = getResponsiveSpacing(options.right);
  }

  return result;
};

/**
 * Get responsive margin object
 * Returns margin values that scale with screen size
 */
export const getResponsiveMargin = (options: {
  vertical?: keyof typeof spacing;
  horizontal?: keyof typeof spacing;
  top?: keyof typeof spacing;
  bottom?: keyof typeof spacing;
  left?: keyof typeof spacing;
  right?: keyof typeof spacing;
}) => {
  const result: {
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
    marginVertical?: number;
    marginHorizontal?: number;
  } = {};

  if (options.vertical) {
    result.marginVertical = getResponsiveSpacing(options.vertical);
  }
  if (options.horizontal) {
    result.marginHorizontal = getResponsiveSpacing(options.horizontal);
  }
  if (options.top) {
    result.marginTop = getResponsiveSpacing(options.top);
  }
  if (options.bottom) {
    result.marginBottom = getResponsiveSpacing(options.bottom);
  }
  if (options.left) {
    result.marginLeft = getResponsiveSpacing(options.left);
  }
  if (options.right) {
    result.marginRight = getResponsiveSpacing(options.right);
  }

  return result;
};

/**
 * Get responsive icon size
 */
export const getResponsiveIconSize = (baseSize: number): number => {
  const screenSize = getScreenSize();

  const scaleFactor = {
    small: 0.9,    // 10% smaller on small screens
    medium: 1,     // Base size
    large: 1.2,    // 20% larger on tablets
    xlarge: 1.4,   // 40% larger on large tablets
  };

  return Math.round(baseSize * scaleFactor[screenSize]);
};

/**
 * Get pixel perfect size
 * Normalizes sizes based on pixel density
 */
export const normalize = (size: number): number => {
  const scale = SCREEN_WIDTH / 375; // Base on iPhone X width
  const newSize = size * scale;

  // Round based on pixel ratio
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
};

/**
 * Get responsive border radius
 */
export const getResponsiveBorderRadius = (baseRadius: number): number => {
  const screenSize = getScreenSize();

  const scaleFactor = {
    small: 0.9,
    medium: 1,
    large: 1.1,
    xlarge: 1.2,
  };

  return Math.round(baseRadius * scaleFactor[screenSize]);
};

// Export dimensions for direct use
export const dimensions = {
  screenWidth: SCREEN_WIDTH,
  screenHeight: SCREEN_HEIGHT,
};

// Helper to determine if device is a tablet
export const isTablet = (): boolean => {
  return SCREEN_WIDTH >= breakpoints.large;
};

// Helper to determine if device is in landscape mode
export const isLandscape = (): boolean => {
  return SCREEN_WIDTH > SCREEN_HEIGHT;
};
